/**
 * Event Service
 * 
 * API service for Admin Event Management operations
 * Base URL: /api/admin/events
 */

import { api } from './api';
import type {
  EventStatisticsResponse,
  AgentEventSummaryResponse,
  EventDetailsResponse,
  EventDetailsFilters,
  PageResponse,
  ReprocessEventsRequest,
  CleanupEventsRequest,
  BatchOperationResponse,
  EventHealthResponse,
} from '@/types';

const BASE_URL = '/admin/events';

export const eventService = {
  /**
   * GET /statistics
   * Dashboard statistics for event overview
   */
  getStatistics: async (): Promise<EventStatisticsResponse> => {
    const response = await api.get<EventStatisticsResponse>(`${BASE_URL}/statistics`);
    return response.data;
  },

  /**
   * GET /pending/by-agent
   * List all agents with pending events, sorted by quantity
   */
  getPendingByAgent: async (): Promise<AgentEventSummaryResponse[]> => {
    const response = await api.get<AgentEventSummaryResponse[]>(`${BASE_URL}/pending/by-agent`);
    return response.data;
  },

  /**
   * GET /stuck
   * List events pending for more than 24 hours (stuck events)
   */
  getStuckEvents: async (params?: {
    page?: number;
    size?: number;
    sort?: string;
  }): Promise<PageResponse<EventDetailsResponse>> => {
    const response = await api.get<PageResponse<EventDetailsResponse>>(`${BASE_URL}/stuck`, {
      params: {
        page: params?.page ?? 0,
        size: params?.size ?? 20,
        sort: params?.sort ?? 'createdAt,desc',
      },
    });
    return response.data;
  },

  /**
   * GET /details
   * List events with optional filters (main table)
   */
  getEventDetails: async (filters?: EventDetailsFilters): Promise<PageResponse<EventDetailsResponse>> => {
    const response = await api.get<PageResponse<EventDetailsResponse>>(`${BASE_URL}/details`, {
      params: {
        agentRef: filters?.agentRef,
        eventType: filters?.eventType,
        status: filters?.status,
        page: filters?.page ?? 0,
        size: filters?.size ?? 20,
        sort: filters?.sort ?? 'createdAt,desc',
      },
    });
    return response.data;
  },

  /**
   * POST /reprocess
   * Mark events as unprocessed for resending to agents
   */
  reprocessEvents: async (request: ReprocessEventsRequest): Promise<BatchOperationResponse> => {
    const response = await api.post<BatchOperationResponse>(`${BASE_URL}/reprocess`, request);
    return response.data;
  },

  /**
   * POST /cleanup
   * Remove old events from database
   */
  cleanupEvents: async (request: CleanupEventsRequest): Promise<BatchOperationResponse> => {
    const response = await api.post<BatchOperationResponse>(`${BASE_URL}/cleanup`, request);
    return response.data;
  },

  /**
   * POST /force-acknowledge
   * Force specific events to be marked as processed manually
   */
  forceAcknowledge: async (eventIds: number[]): Promise<BatchOperationResponse> => {
    const response = await api.post<BatchOperationResponse>(`${BASE_URL}/force-acknowledge`, eventIds);
    return response.data;
  },

  /**
   * GET /health
   * Health check of event system
   */
  getHealth: async (): Promise<EventHealthResponse> => {
    const response = await api.get<EventHealthResponse>(`${BASE_URL}/health`);
    return response.data;
  },
};
